import { Observable, from, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import {
  TextCleanUpService
} from "@services/file-translations/file-translation-validation/text-clean-up/text-clean-up.service";
import MsgReader from '@kenjiuno/msgreader';

interface MsgRecipient {
  name?: string;
  email?: string;
  recipType?: string;
}

interface MsgFileData {
  subject?: string;
  senderName?: string;
  senderEmail?: string;
  body?: string;
  bodyHtml?: string;
  html?: Uint8Array;
  compressedRtf?: Uint8Array;
  headers?: string;
  recipients?: MsgRecipient[];
  attachments?: any[];
  rawProps?: any[];
}

export class MsgTextExtractorService {
  constructor(private textCleanUpService: TextCleanUpService) {}

  extractText(file: File): Observable<string> {
    return from(
      new Promise<string>((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = (e) => {
          try {
            if (!e.target?.result) {
              reject(new Error('Failed to read MSG file content'));
              return;
            }

            const arrayBuffer = e.target.result as ArrayBuffer;

            const msgFileBuffer = new Uint8Array(arrayBuffer);

            const parserConfig = { includeRawProps: true };

            const msgReader = new MsgReader(msgFileBuffer);
            msgReader.parserConfig = parserConfig;
            const msgData = msgReader.getFileData() as MsgFileData;

            let textContent = '';

            if (msgData.subject) {
              textContent += `Subject: ${msgData.subject}\n\n`;
            }

            if (msgData.senderName || msgData.senderEmail) {
              textContent += `From: ${msgData.senderName || msgData.senderEmail}\n\n`;
            }

            let bodyExtracted = false;

            if (msgData.body && msgData.body.trim().length > 0) {
              textContent += msgData.body;
              bodyExtracted = true;
              console.log('Body extracted from standard body property');
            }
            else if (msgData.bodyHtml && msgData.bodyHtml.trim().length > 0) {
              const strippedHtml = this.stripHtml(msgData.bodyHtml);

              if (strippedHtml.length > 0) {
                textContent += strippedHtml;
                bodyExtracted = true;
                console.log('Body extracted from bodyHtml property');
              }
            }
            else if (msgData.rawProps && msgData.rawProps.length > 0) {
              const htmlProp = msgData.rawProps.find(prop =>
                prop.propertyTag === '10130102' && prop.value instanceof Uint8Array);

              if (htmlProp && htmlProp.value) {
                try {
                  const htmlContent = new TextDecoder().decode(htmlProp.value);
                  const strippedHtml = this.stripHtml(htmlContent);

                  if (strippedHtml.length > 0) {
                    textContent += strippedHtml;
                    bodyExtracted = true;
                    console.log('Body extracted from raw HTML property');
                  }
                } catch (htmlError) {
                  console.error('Error extracting HTML from raw property:', htmlError);
                }
              }
            }
            else if (msgData.headers && msgData.headers.length > 0) {
              const bodyMatch = msgData.headers.match(/\r?\n\r?\n([\s\S]*?)$/);
              if (bodyMatch && bodyMatch[1] && bodyMatch[1].trim().length > 0) {
                textContent += bodyMatch[1].trim();
                bodyExtracted = true;
                console.log('Body extracted from headers');
              }
            }

            if (!bodyExtracted) {
              textContent += "[No body content found in the message]";
              console.log('No body content found');
            }

            if (msgData.recipients && msgData.recipients.length > 0) {
              textContent += '\n\nTo: ';
              textContent += msgData.recipients
                .map((recipient: MsgRecipient) => recipient.name || recipient.email)
                .join('; ');
            }

            const cleanedText = this.textCleanUpService.createSample(textContent);

            resolve(cleanedText);
          } catch (error) {
            reject(new Error(`MSG parsing failed: ${error instanceof Error ? error.message : 'Unknown error'}`));
          }
        };

        reader.onerror = () => reject(new Error('Error reading MSG file'));
        reader.readAsArrayBuffer(file);
      })
    ).pipe(
      catchError(error => throwError(() => new Error(`MSG extraction failed: ${error.message || 'Unknown error'}`)))
    );
  }

  private stripHtml(html: string): string {
    if (!html) return '';

    try {
      const doc = document.implementation.createHTMLDocument('');
      doc.documentElement.innerHTML = html;

      let text = doc.body.textContent || '';

      text = text.replace(/\s+/g, ' ')
        .replace(/\n\s*\n/g, '\n\n')
        .trim();

      return text;
    } catch (error) {
      console.error('Error stripping HTML:', error);

      return html
        .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '')
        .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '')
        .replace(/<[^>]+>/g, ' ')
        .replace(/&nbsp;/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
    }
  }
}
